import i18n, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LOCALES = ['de-DE', 'en-US'] as const;
export type Locale = (typeof LOCALES)[number];

// Namespaces are split into individual JSON files per locale.
// A compatibility namespace `compat` mirrors the old shape so existing
// calls like t('auth.login') continue to work. New code should use
// useTranslation('<ns>') and keys without the prefix.
export const NAMESPACES = [
  'compat',
  'common',
  'auth',
  'welcome',
  'errors',
  'navigation',
  'settings',
  'users',
  'portalRoles',
  'permissions',
  'userGroups',
  'customer',
  'time',
  'pagination',
  'actions',
  'searchCombobox',
  'metadata',
  'contracts',
  'validation',
  'customers',
  'objects',
  'operations',
  'objectCategories',
  'activity',
  'notifications',
  'zod',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_LOCALE: Locale = 'de-DE';
export const DEFAULT_NS: Namespace = 'compat';

let initialized = false;

export async function loadI18n(): Promise<I18nInstance> {
  // Resources are registered during init; this remains for API compatibility.
  return i18n;
}

export function ensureI18n(): I18nInstance {
  if (!initialized) {
    // Eagerly import all JSON files so the first render has all messages.
    // Vite's import.meta.glob with eager: true turns them into static imports.
    const deModules = import.meta.glob<{ default: unknown }>('../locales/de-DE/*.json', {
      eager: true,
    });
    const enModules = import.meta.glob<{ default: unknown }>('../locales/en-US/*.json', {
      eager: true,
    });

    const loadLocale = (modules: Record<string, { default: unknown }>) => {
      const ns: Record<string, unknown> = {};
      Object.entries(modules).forEach(([path, mod]) => {
        const name = path.split('/').pop()!.replace('.json', '');
        ns[name] = mod.default;
      });
      // Build the compatibility namespace with nested groups by
      // copying and dropping the explicit 'compat' and 'zod' entries.
      const compatNs: Record<string, unknown> = { ...ns };
      delete (compatNs as Record<string, unknown>).compat;
      delete (compatNs as Record<string, unknown>).zod;
      return { ns, compatNs } as const;
    };

    const { ns: deNs, compatNs: deCompat } = loadLocale(deModules);
    const { ns: enNs, compatNs: enCompat } = loadLocale(enModules);

    i18n.use(initReactI18next).init({
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...LOCALES],
      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NS,
      interpolation: { escapeValue: false },
      returnNull: false,
      initImmediate: false,
      resources: {
        'de-DE': { compat: deCompat, ...(deNs as Record<string, unknown>) },
        'en-US': { compat: enCompat, ...(enNs as Record<string, unknown>) },
      },
    });
    initialized = true;
  }
  return i18n;
}

export default i18n;
